(function(global) {
  function createArMode({ translate, animateCameraToInitial, getPreviewCanvas }) {
    
    // ===============================================================
    // 1. CORE & UTILS
    // ===============================================================
    const translateFn = typeof translate === 'function' ? translate : value => value;
    const animateCamera = typeof animateCameraToInitial === 'function' ? animateCameraToInitial : () => {};
    const resolvePreviewCanvas = typeof getPreviewCanvas === 'function' ? getPreviewCanvas : () => null;

    const arState = {
      supportChecked: false,
      webXrSupported: false,
      isMobile: false
    };
    const AR_YAW_OFFSET = -Math.PI / 2;
    const DEFAULT_AR_LIGHT_INTENSITY = {
      ambient: 1.35,
      directional: 1.15
    };

    function clamp(value, min, max) {
      return Math.min(Math.max(value, min), max);
    }

    function isMobileDevice() {
      if (typeof navigator === 'undefined') return false;
      return (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i).test(navigator.userAgent || '');
    }

    function isAndroid() {
      if (typeof navigator === 'undefined') return false;
      return (/Android/i).test(navigator.userAgent || '');
    }

    function isIos() {
      if (typeof navigator === 'undefined') return false;
      return (/iPad|iPhone|iPod/i).test(navigator.userAgent || '');
    }

    // ===============================================================
    // 2. UI & OVERLAY MANAGEMENT
    // ===============================================================
    function updateArTooltip(message) {
      const tooltip = document.getElementById('oryx-ar-tooltip');
      if (!tooltip) return;
      const text = message || '';
      tooltip.textContent = text;
      tooltip.classList.toggle('oryx-ar-tooltip--visible', Boolean(text));
    }

    function setPreviewArState(isActive) {
      const preview = document.getElementById('oryx-preview');
      if (preview) {
        preview.classList.toggle('oryx-ar-active', Boolean(isActive));
      }

      const arExitBtn = document.getElementById('oryx-ar-exit-btn');
      if (arExitBtn) {
        if (isActive) {
          arExitBtn.hidden = false;
          arExitBtn.disabled = false;
          arExitBtn.setAttribute('aria-hidden', 'false');
          arExitBtn.style.display = 'flex'; 
        } else {
          arExitBtn.hidden = true;
          arExitBtn.disabled = true;
          arExitBtn.setAttribute('aria-hidden', 'true');
          arExitBtn.style.display = 'none';
        }
      }
    }

    function isPreviewArActive() {
      const preview = document.getElementById('oryx-preview');
      return Boolean(preview && preview.classList.contains('oryx-ar-active'));
    }

    function getArAssetsFromDom() {
      const container = document.getElementById('configurator-container');
      return {
        glbUrl: container?.dataset?.arGlbUrl || '',
        usdzUrl: container?.dataset?.arUsdzUrl || ''
      };
    }

    // ===============================================================
    // 3. SCENE ENVIRONMENT & LIGHTING
    // ===============================================================
    function applyArEnvironmentOverrides(viewerObj) {
      const scene = viewerObj?.scene;
      if (!scene) return;

      const skybox = viewerObj.skybox;

      if (!viewerObj.arEnvironmentBackup) {
        const pipelines = scene.postProcessRenderPipelineManager?.supportedPipelines || [];
        const activePipeline = pipelines.find(p => p.name === 'oryx-pipeline');

        viewerObj.arEnvironmentBackup = {
          environmentTexture: scene.environmentTexture,
          environmentIntensity: scene.environmentIntensity,
          clearColor: scene.clearColor?.clone?.() || scene.clearColor,
          skyboxEnabled: skybox ? skybox.isEnabled?.() ?? skybox.isVisible : null,
          pipelineRef: activePipeline
        };
      }

      scene.clearColor = new BABYLON.Color4(0, 0, 0, 0);
      if (skybox) {
        if(skybox.setEnabled) skybox.setEnabled(false);
        skybox.isVisible = false;
      }

      if (viewerObj.arEnvironmentBackup.pipelineRef) {
        scene.postProcessRenderPipelineManager.detachCamerasFromRenderPipeline('oryx-pipeline', scene.cameras);
      }
    }

    function restoreArEnvironment(viewerObj) {
      const scene = viewerObj?.scene;
      const backup = viewerObj?.arEnvironmentBackup;
      if (!scene || !backup) return;

      scene.environmentTexture = backup.environmentTexture ?? scene.environmentTexture;
      if (typeof backup.environmentIntensity === 'number') {
        scene.environmentIntensity = backup.environmentIntensity;
      }
      if (backup.clearColor) {
        scene.clearColor = backup.clearColor.clone?.() || backup.clearColor;
      }

      const skybox = viewerObj.skybox;
      if (skybox && typeof backup.skyboxEnabled === 'boolean') {
        if(skybox.setEnabled) skybox.setEnabled(backup.skyboxEnabled);
        skybox.isVisible = backup.skyboxEnabled;
      }

      if (backup.pipelineRef) {
        scene.postProcessRenderPipelineManager.attachCamerasToRenderPipeline('oryx-pipeline', scene.cameras);
      }

      viewerObj.arEnvironmentBackup = null;
    }

    function enableArLighting(viewerObj) {
      const scene = viewerObj?.scene;
      const camera = viewerObj?.xrExperience?.baseExperience?.camera || viewerObj?.camera;
      if (!scene || !camera || viewerObj.arLighting) return;

      const ambient = new BABYLON.HemisphericLight('oryx-ar-hemi', new BABYLON.Vector3(0, 1, 0), scene);
      ambient.intensity = DEFAULT_AR_LIGHT_INTENSITY.ambient;

      const followLight = new BABYLON.DirectionalLight('oryx-ar-follow', new BABYLON.Vector3(0, -1, 0), scene);
      followLight.intensity = DEFAULT_AR_LIGHT_INTENSITY.directional;
      followLight.shadowEnabled = false;

      const updateFollowLight = () => {
        const cameraPos = camera.globalPosition || camera.position;
        if (!cameraPos) return;
        followLight.position.copyFrom(cameraPos);
        const forward = typeof camera.getForwardRay === 'function' ? camera.getForwardRay()?.direction : null;
        if (forward) {
          followLight.direction = forward.clone();
        }
      };

      const observer = scene.onBeforeRenderObservable.add(updateFollowLight);

      viewerObj.arLighting = { ambient, followLight, observer };
    }

    function disableArLighting(viewerObj) {
      const scene = viewerObj?.scene;
      const lighting = viewerObj?.arLighting;
      if (!scene || !lighting) return;

      if (lighting.observer && scene.onBeforeRenderObservable) {
        scene.onBeforeRenderObservable.remove(lighting.observer);
      }
      lighting.ambient?.dispose?.();
      lighting.followLight?.dispose?.();
      viewerObj.arLighting = null;
    }

    // ===============================================================
    // 4. LOGIC - SCALING & TRANSFORM
    // ===============================================================
    function getArScaleFactor(viewerObj) {
      const defaultScale = 1.0;
      const root = viewerObj?.rootTransform;
      if (!root?.getHierarchyBoundingVectors) return defaultScale;

      try {
        root.computeWorldMatrix(true);
        const { min, max } = root.getHierarchyBoundingVectors(true);
        const size = max.subtract(min);
        const longestSide = Math.max(size.x, size.y, size.z);

        if (!Number.isFinite(longestSide) || longestSide <= 0) return defaultScale;

        const targetLongestSide = 0.25; // 25cm
        const scaleFactor = targetLongestSide / longestSide;

        // WICHTIG: Min 0.0001, damit riesige MM-Modelle klein genug werden
        return clamp(scaleFactor, 0.0001, 10);
      } catch (error) {
        console.warn('AR Scale Error', error);
        return defaultScale;
      }
    }

    function applyArScale(viewerObj, isEnabled) {
      const rootTransform = viewerObj?.rootTransform;
      if (!rootTransform) return;

      if (isEnabled) {
        viewerObj.arOriginalScaling = viewerObj.arOriginalScaling || rootTransform.scaling.clone();
        const arScaleFactor = getArScaleFactor(viewerObj);
        rootTransform.scaling = viewerObj.arOriginalScaling.scale(arScaleFactor);
      } else if (viewerObj.arOriginalScaling) {
        rootTransform.scaling = viewerObj.arOriginalScaling.clone();
        viewerObj.arOriginalScaling = null;
      }
    }

    function parseHitResult(hitResult) {
      if (!hitResult) return null;
      const result = {};

      if (Array.isArray(hitResult.transformationMatrix) && hitResult.transformationMatrix.length === 16 && BABYLON?.Matrix) {
        const matrix = BABYLON.Matrix.FromArray(hitResult.transformationMatrix);
        const scaling = new BABYLON.Vector3();
        const rotation = new BABYLON.Quaternion();
        const position = new BABYLON.Vector3();
        matrix.decompose(scaling, rotation, position);
        result.position = position;
        result.rotation = rotation;
      }

      if (hitResult.position && !result.position) {
        result.position = hitResult.position.clone?.() ?? BABYLON.Vector3.FromArray(hitResult.position);
      }
      if (hitResult.normal) {
        result.normal = hitResult.normal.clone?.() ?? BABYLON.Vector3.FromArray(hitResult.normal);
      }
      return result.position ? result : null;
    }

    function applyHitToRootTransform(viewerObj, placementState, { forceRotation = false } = {}) {
      const root = viewerObj?.rootTransform;
      const hit = placementState?.lastHit;
      if (!root || !hit?.position) return;

      root.position.copyFrom(hit.position);

      const shouldAlignRotation = !viewerObj?.arManualRotation || forceRotation;
      if (!shouldAlignRotation) return;

      let targetRotation = null;
      if (hit.rotation) {
        targetRotation = hit.rotation.clone();
      } else if (hit.normal) {
        const up = hit.normal.normalize();
        let forward = BABYLON.Vector3.Cross(BABYLON.Vector3.Right(), up);
        if (forward.lengthSquared() < 0.0001) forward = BABYLON.Vector3.Forward();
        else forward = forward.normalize();
        targetRotation = BABYLON.Quaternion.FromLookDirectionLH(forward, up);
      }

      if (targetRotation) {
        const yawOffset = BABYLON.Quaternion.FromEulerAngles(0, AR_YAW_OFFSET, 0);
        targetRotation.multiplyInPlace(yawOffset);
        root.rotationQuaternion = targetRotation;
      }
    }

    // ===============================================================
    // 5. INTERACTION & HIT TEST
    // ===============================================================
    function attachArHitTest(viewerObj) {
      const xrHelper = viewerObj?.xrExperience;
      if (!xrHelper?.baseExperience) return;

      const fm = xrHelper.baseExperience.featuresManager;
      if (!fm) return;

      const hitTest = fm.enableFeature(BABYLON.WebXRFeatureName.HIT_TEST, 'latest', {
        preferredHandedness: 'none',
        xrInput: xrHelper.input,
        entityTypes: ['plane']
      });

      fm.enableFeature(BABYLON.WebXRFeatureName.POINTER_SELECTION, 'stable', {
        xrInput: xrHelper.input,
        enablePointerSelectionOnAllControllers: true
      });

      const placementState = viewerObj.arPlacementState || {
        isDragging: false,
        hasPlaced: false,
        lastHit: null
      };
      viewerObj.arPlacementState = placementState;

      if (hitTest?.onHitTestResultObservable && viewerObj.arCursor) {
        const dragHint = translateFn('messages.ar_drag_place_hint');
        const placeHint = translateFn('messages.ar_place_hint');

        // Material des Rings auf Unlit setzen für bessere Sichtbarkeit
        if (viewerObj.arCursor.material) {
           viewerObj.arCursor.material.disableLighting = true;
           viewerObj.arCursor.material.emissiveColor = new BABYLON.Color3(1, 0.9, 0);
        }

        hitTest.onHitTestResultObservable.add(results => {
          const hasSurface = results.length > 0;
          viewerObj.arCursor.isVisible = hasSurface;

          if (hasSurface) {
            const hitInfo = parseHitResult(results[0]);
            if (hitInfo) {
              placementState.lastHit = hitInfo;
              viewerObj.arCursor.position.copyFrom(hitInfo.position);

              if (hitInfo.normal) {
                const yAxis = hitInfo.normal.normalize();
                const xAxis = BABYLON.Vector3.Cross(BABYLON.Vector3.Right(), yAxis).normalize();
                const zAxis = BABYLON.Vector3.Cross(yAxis, xAxis).normalize();
                viewerObj.arCursor.rotationQuaternion = BABYLON.Quaternion.RotationQuaternionFromAxis(xAxis, yAxis, zAxis);
              } else if (hitInfo.rotation) {
                viewerObj.arCursor.rotationQuaternion = hitInfo.rotation.clone();
              }
              
              if (viewerObj.arCursor.scaling) {
                viewerObj.arCursor.scaling.setAll(1);
              }

              if (!placementState.hasPlaced || placementState.isDragging) {
                applyHitToRootTransform(viewerObj, placementState, { forceRotation: true });
              }
            }
            updateArTooltip(dragHint);
          } else {
            updateArTooltip(placeHint);
          }
        });
      }

      // Touch Dragging Logic
      const scene = viewerObj.scene;
      if (scene?.onPointerObservable && typeof BABYLON !== 'undefined') {
        const updateRootTransformFromCursor = () => {
          const root = viewerObj?.rootTransform;
          const cursor = viewerObj?.arCursor;
          if (!placementState.isDragging || !root || !cursor?.isVisible || !cursor.position) return;
          root.position.copyFrom(cursor.position);
          
          const camera = viewerObj?.scene?.activeCamera || viewerObj?.camera;
          if (camera) {
             const dir = camera.position.subtract(root.position).normalize();
             root.rotationQuaternion = BABYLON.Quaternion.FromLookDirectionLH(dir, BABYLON.Vector3.Up());
          }
        };

        scene.onPointerObservable.add(pointerInfo => {
          if (!pointerInfo) return;
          if (pointerInfo.type === BABYLON.PointerEventTypes.POINTERDOWN) {
            placementState.isDragging = true;
            placementState.hasPlaced = true;
            viewerObj.arManualRotation = false;
            updateRootTransformFromCursor();
          } else if (pointerInfo.type === BABYLON.PointerEventTypes.POINTERUP) {
            placementState.isDragging = false;
          } else if (pointerInfo.type === BABYLON.PointerEventTypes.POINTERMOVE) {
            updateRootTransformFromCursor();
          }
        });
      }

      // Cleanup bei Session Ende (verhindert Crash)
      const sessionManager = xrHelper.baseExperience.sessionManager;
      if (sessionManager) {
        if (sessionManager.onXRSessionInitObservable) {
          sessionManager.onXRSessionInitObservable.add(() => {
            viewerObj.arOriginalScaling = viewerObj.arOriginalScaling || viewerObj.rootTransform?.scaling.clone() || null;
            applyArEnvironmentOverrides(viewerObj);
            applyArScale(viewerObj, true);
          });
        }
        if (sessionManager.onXRSessionEndedObservable) {
          sessionManager.onXRSessionEndedObservable.add(() => {
            console.log("AR Session Ended Event triggered");
            updateArTooltip('');
            if (viewerObj.arCursor) viewerObj.arCursor.isVisible = false;
            
            disableArLighting(viewerObj);
            restoreArEnvironment(viewerObj);
            
            if (document.body) document.body.classList.remove('oryx-ar-mode');
            
            const arExitBtn = document.getElementById('oryx-ar-exit-btn');
            if (arExitBtn) {
              arExitBtn.hidden = true;
              arExitBtn.disabled = true;
              arExitBtn.setAttribute('aria-hidden', 'true');
              arExitBtn.style.display = 'none';
            }
            
            const arBtn = document.getElementById('oryx-ar-btn');
            resetAfterArExit(viewerObj, arBtn);
          });
        }
      }
    }

    function setupArTouchRotation(viewerObj, canvasTarget) {
      if (!viewerObj?.rootTransform) return;
      const touchTarget = document.body;
      if (viewerObj.arTouchRotationSetup) return;

      const rotationState = { isDragging: false, startX: 0, initialRotation: 0 };
      const getClientX = e => (e.touches ? e.touches[0].clientX : e.clientX);
      const isArActive = () => isPreviewArActive();

      const startDrag = e => {
        if (!isArActive()) return;
        if (viewerObj.arPlacementState?.isDragging) return;

        rotationState.isDragging = true;
        rotationState.startX = getClientX(e);
        viewerObj.arManualRotation = true;
        
        if (viewerObj.arPlacementState) viewerObj.arPlacementState.hasPlaced = true;
        
        if (viewerObj.rootTransform.rotationQuaternion) {
          rotationState.initialRotation = viewerObj.rootTransform.rotationQuaternion.toEulerAngles().y;
        } else {
          rotationState.initialRotation = viewerObj.rootTransform.rotation?.y || 0;
        }
      };

      const updateRotation = e => {
        if (!rotationState.isDragging || !isArActive()) return;
        const currentX = getClientX(e);
        const deltaX = currentX - rotationState.startX;
        const newRotation = rotationState.initialRotation + (deltaX * 0.005);

        if (viewerObj.rootTransform.rotationQuaternion) {
          const euler = viewerObj.rootTransform.rotationQuaternion.toEulerAngles();
          viewerObj.rootTransform.rotationQuaternion = BABYLON.Quaternion.FromEulerAngles(euler.x, newRotation, euler.z);
        } else if (viewerObj.rootTransform.rotation) {
          viewerObj.rootTransform.rotation.y = newRotation;
        }
      };

      const endDrag = () => { rotationState.isDragging = false; };

      ['pointerdown', 'touchstart'].forEach(evt => touchTarget.addEventListener(evt, startDrag, { passive: true }));
      ['pointermove', 'touchmove'].forEach(evt => touchTarget.addEventListener(evt, updateRotation, { passive: true }));
      ['pointerup', 'pointercancel', 'touchend', 'touchcancel', 'mouseleave'].forEach(evt => touchTarget.addEventListener(evt, endDrag, { passive: true }));

      viewerObj.arTouchRotationSetup = true;
    }


    // ===============================================================
    // 6. NATIVE AR FALLBACKS (iOS/Android)
    // ===============================================================
    function openQuickLook(url) {
      if (!url) return false;
      const anchor = document.createElement('a');
      anchor.rel = 'ar';
      anchor.href = url;
      anchor.style.display = 'none';
      const img = document.createElement('img');
      img.alt = '';
      anchor.appendChild(img);
      document.body.appendChild(anchor);
      anchor.click();
      requestAnimationFrame(() => anchor.remove());
      return true;
    }

    function openSceneViewerIntent(url) {
      if (!url) return false;
      const fallback = `${window.location.origin}${window.location.pathname}${window.location.search || ''}`;
      const intentUrl = `intent://arvr.google.com/scene-viewer/1.0?file=${encodeURIComponent(url)}&mode=ar_preferred#Intent;scheme=https;package=com.google.ar.core;S.browser_fallback_url=${encodeURIComponent(fallback)};end;`;
      window.location.href = intentUrl;
      return true;
    }


    // ===============================================================
    // 7. WEBXR SESSION MANAGER
    // ===============================================================
    async function startWebXrAr(viewerObj, arBtn) {
      if (!viewerObj?.scene) {
        updateArTooltip(translateFn('messages.ar_launch_failed'));
        return false;
      }

      try {
        updateArTooltip(translateFn('messages.ar_preparing'));
        const dragPlaceHint = translateFn('messages.ar_drag_place_hint');

        const domOverlayRoot = document.getElementById('oryx-ar-ui-overlay') || document.body;

        if (!viewerObj.xrExperience) {
          const uiOptions = {
            sessionMode: 'immersive-ar',
            referenceSpaceType: 'local-floor',
            domOverlay: { root: domOverlayRoot }
          };

          viewerObj.xrExperience = await viewerObj.scene.createDefaultXRExperienceAsync({
            uiOptions,
            disableTeleportation: true,
            disableDefaultUI: true,
            requiredFeatures: ['local-floor'],
            optionalFeatures: ['hit-test', 'dom-overlay']
          });
          
          attachArHitTest(viewerObj);
        }

        applyArEnvironmentOverrides(viewerObj);

        await viewerObj.xrExperience.baseExperience.enterXRAsync('immersive-ar', 'local-floor');

        const sessionManager = viewerObj?.xrExperience?.baseExperience?.sessionManager;
        const isInXr = Boolean(sessionManager?.session);

        if (isInXr) {
          if (document.body) document.body.classList.add('oryx-ar-mode');
          
          if (viewerObj.arCursor) {
            viewerObj.arCursor.isVisible = false; 
            if(viewerObj.arCursor.scaling) viewerObj.arCursor.scaling.setAll(1);
          }

          viewerObj.arOriginalScaling = viewerObj.arOriginalScaling || viewerObj.rootTransform?.scaling.clone() || null;
          applyArScale(viewerObj, true);

          enableArLighting(viewerObj);
          console.info('ORYX AR started');

          const arExitBtn = document.getElementById('oryx-ar-exit-btn');
          if (arExitBtn) {
            arExitBtn.hidden = false;
            arExitBtn.disabled = false;
            arExitBtn.removeAttribute('hidden');
            arExitBtn.setAttribute('aria-hidden', 'false');
            arExitBtn.style.display = 'flex';
          }
          if (arBtn) arBtn.setAttribute('aria-pressed', 'true');

          updateArTooltip(dragPlaceHint);
          setPreviewArState(true);
          return true;
        }

        throw new Error("XR Session not active");

      } catch (error) {
        console.warn('WebXR AR start failed', error);
        
        // Debug Feedback
        // alert('AR Error: ' + (error.message || error));

        updateArTooltip(translateFn('messages.ar_launch_failed'));
        restoreArEnvironment(viewerObj);
        setPreviewArState(false);
        if (document.body) document.body.classList.remove('oryx-ar-mode');
        return false;
      }
    }

    async function stopWebXrAr(viewerObj, arBtn) {
      try {
        const xrHelper = viewerObj?.xrExperience?.baseExperience;
        if (xrHelper && xrHelper.state === BABYLON.WebXRState.IN_XR) {
          await xrHelper.exitXRAsync();
        }
      } catch (error) {
        console.warn('WebXR AR stop failed', error);
      }
      // KEIN manueller Reset hier! Das macht der Event Listener 'onXRSessionEndedObservable'
    }


    // ===============================================================
    // 8. CONTROL SETUP & EXPORT
    // ===============================================================
    async function ensureWebXrSupport() {
      if (arState.supportChecked) return arState.webXrSupported;
      arState.supportChecked = true;
      if (typeof navigator === 'undefined' || !navigator.xr || typeof navigator.xr.isSessionSupported !== 'function') {
        arState.webXrSupported = false;
        return false;
      }
      try {
        arState.webXrSupported = await navigator.xr.isSessionSupported('immersive-ar');
      } catch (error) {
        console.warn('WebXR support check failed', error);
        arState.webXrSupported = false;
      }
      return arState.webXrSupported;
    }

    function setupArControl(viewerObj) {
      const arBtn = document.getElementById('oryx-ar-btn');
      const arExitBtn = document.getElementById('oryx-ar-exit-btn');
      if (!arBtn) return;

      const { glbUrl, usdzUrl } = getArAssetsFromDom();
      arBtn.setAttribute('type', 'button');
      arState.isMobile = isMobileDevice();

      if (arExitBtn) {
        arExitBtn.hidden = true;
        arExitBtn.setAttribute('aria-hidden', 'true');
        arExitBtn.disabled = true;
        arExitBtn.style.display = 'none';
      }

      if (!arState.isMobile) {
        arBtn.hidden = false;
        arBtn.setAttribute('aria-hidden', 'false');
        arBtn.disabled = false;

        const mobileOnlyMessage = translateFn('messages.ar_mobile_only');
        arBtn.addEventListener('click', event => {
          if (event) {
            event.preventDefault();
            event.stopPropagation();
          }
          alert(mobileOnlyMessage);
        }, { capture: true });
        return;
      }

      setupArTouchRotation(viewerObj, document.body);

      // Check Support
      ensureWebXrSupport().then(isSupported => {
        arState.webXrSupported = isSupported;
        const hasFallback = Boolean(glbUrl || usdzUrl);
        if (!isSupported && !hasFallback) {
          arBtn.hidden = true;
          return;
        }
        arBtn.hidden = false;
        arBtn.disabled = false;
      });

      // === CLICK HANDLER MIT DEBUG-INFOS ===
      const oryxArGlobal = global.OryxAR || (global.OryxAR = {});
      oryxArGlobal.startSession = () => startWebXrAr(viewerObj, arBtn);
      oryxArGlobal.stopSession = () => stopWebXrAr(viewerObj, arBtn);
      oryxArGlobal.openQuickLook = () => openQuickLook(usdzUrl);
      oryxArGlobal.openSceneViewerIntent = () => openSceneViewerIntent(glbUrl);
    }

    return {
      setupArControl,
      isMobileDevice,
      isAndroid,
      isIos,
      setPreviewArState
    };
  }

  global.OryxConfiguratorArMode = { create: createArMode };
})(window);
